import React, { useState, useEffect } from "react";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import UserModal from "./UserModal";

const PAGE_SIZE = 10;

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  // Đã xóa state divisions
  const [search, setSearch] = useState("");
  const [isUserPopupOpen, setIsUserPopupOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    fetchUsers();
    fetchBranches();
    fetchDepartments();
    // Đã xóa fetchDivisions() để hết lỗi 404
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axiosClient.get(API_ENDPOINTS.users);
      setUsers(res.data || []);
    } catch (error) {
      console.error("❌ Error loading users:", error);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await axiosClient.get(API_ENDPOINTS.branches);
      setBranches(res.data);
    } catch (error) { console.error("Error branches:", error); }
  };

  const fetchDepartments = async () => {
    try {
      const res = await axiosClient.get(API_ENDPOINTS.departments);
      setDepartments(res.data);
    } catch (error) { console.error("Error departments:", error); }
  };

  const handleExport = async () => {
    try {
      const response = await axiosClient.get(`${API_ENDPOINTS.users}/export`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `UserList_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Export failed!");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("File", file); 

    try {
      const res = await axiosClient.post(`${API_ENDPOINTS.users}/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      if (res.data.success) {
        alert("🎉 Imported successfully!");
        fetchUsers();
      }
      e.target.value = null; 
    } catch (error) {
      alert("Import error: " + (error.response?.data?.message || "Unknown error"));
      e.target.value = null;
    }
  };

  const handleSaveUser = async (data) => {
    setServerError("");
    try {
      // KIỂM TRA CHẾ ĐỘ: Nếu có editingUser thì chắc chắn là PUT
      if (editingUser) {
        const targetId = data.id || editingUser.id;
        
        if (!targetId) {
          alert("Error: User ID not found for update.");
          return;
        }

        await axiosClient.put(`${API_ENDPOINTS.users}/${targetId}`, data);
        alert("🎉 Update successful!");
      } else {
        // Chế độ tạo mới
        await axiosClient.post(`${API_ENDPOINTS.auth}/register`, data);
        alert("🎉 Registration successful!");
      }
      fetchUsers();
      setIsUserPopupOpen(false);
      setEditingUser(null);
    } catch (err) {
      setServerError(err.response?.data || "System error occurred.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axiosClient.delete(`${API_ENDPOINTS.users}/${id}`);
      fetchUsers();
    } catch (error) { alert("Could not delete this user."); }
  };

  const filteredUsers = users.filter(u => (u.userName || "").toLowerCase().includes(search.toLowerCase()));
  const totalPages = Math.ceil(filteredUsers.length / PAGE_SIZE);
  const currentUsers = filteredUsers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h4 mb-0 text-gray-800 fw-bold">USER MANAGEMENT</h1>
        <div className="d-flex gap-2">
          <div className="btn-group shadow-sm">
            <button className="btn btn-light border" onClick={handleExport} title="Export to Excel">
              <i className="fas fa-file-export text-primary"></i>
            </button>
            <label className="btn btn-light border mb-0" style={{ cursor: "pointer" }} title="Import from Excel">
              <i className="fas fa-file-import text-warning"></i>
              <input type="file" accept=".xlsx" hidden onChange={handleImport} />
            </label>
          </div>

          <button className="btn btn-primary rounded-pill px-4 shadow-sm fw-bold" onClick={() => { setEditingUser(null); setServerError(""); setIsUserPopupOpen(true); }}>
            <i className="fas fa-plus me-2"></i>Add New User
          </button>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <div className="input-group input-group-sm" style={{ width: "300px" }}>
            <span className="input-group-text bg-light border-end-0" style={{ borderRadius: "20px 0 0 20px" }}>
              <i className="fas fa-search text-muted"></i>
            </span>
            <input 
              type="text" 
              className="form-control border-start-0 bg-light" 
              style={{ borderRadius: "0 20px 20px 0" }}
              placeholder="Search by username..." 
              value={search} 
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }} 
            />
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="text-center" style={{ width: "80px" }}>No.</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.length > 0 ? (
                  currentUsers.map((u, index) => (
                    <tr key={u.id}>
                      <td className="text-center">{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                      <td className="fw-bold text-primary">{u.userName}</td>
                      <td>
                        <span className="badge bg-soft-info text-info border border-info px-3">
                          {u.role}
                        </span>
                      </td>
                      <td>
                        <span className={`badge rounded-pill px-3 ${u.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
                          {u.status}
                        </span>
                      </td>
                      <td className="text-center">
                        <button 
                          className="btn btn-sm btn-outline-primary border-0 me-2" 
                          onClick={async () => {
                            try {
                              const res = await axiosClient.get(`${API_ENDPOINTS.users}/${u.id}`);
                              // Đảm bảo dữ liệu có ID phẳng để logic Edit chuẩn xác
                              const flatData = {
                                ...res.data,
                                id: res.data.id || u.id
                              };
                              setEditingUser(flatData);
                              setIsUserPopupOpen(true);
                            } catch (err) { 
                              alert("Failed to load user details.");
                            }
                          }}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button 
                          className="btn btn-sm btn-outline-danger border-0" 
                          onClick={() => handleDelete(u.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-5 text-muted">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {totalPages > 1 && (
          <div className="card-footer bg-white border-0 py-3 text-center">
              <button 
                className="btn btn-sm btn-light mx-1 border shadow-sm rounded-circle" 
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <span className="mx-3 fw-bold text-primary small">Page {currentPage} of {totalPages}</span>
              <button 
                className="btn btn-sm btn-light mx-1 border shadow-sm rounded-circle" 
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                <i className="fas fa-chevron-right"></i>
              </button>
          </div>
        )}
      </div>

      <UserModal
        show={isUserPopupOpen}
        initialData={editingUser}
        onClose={() => { 
          setIsUserPopupOpen(false); 
          setEditingUser(null); 
        }}
        branches={branches}
        departments={departments}
        onSave={handleSaveUser}
        serverError={serverError}
      />
    </div>
  );
};

export default UserList;